/**
 * Écran de fin de leçon style Duolingo
 * Avec statistiques, animations et célébration
 * Now with full translations support for all languages
 */
import React, { useEffect, useState } from 'react';
import { Trophy, Zap, Target, Star, ArrowRight, Home, Sparkles, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import Confetti from './Confetti';
import { useGameSounds } from '../hooks/useGameSounds';

const LessonComplete = ({ 
  xpEarned, 
  correctAnswers, 
  totalQuestions,
  timeSpent,
  onNextLesson,
  onBackToDashboard,
  hasNextLesson,
  lessonTitle,
  lessonContent,
  t
}) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [animatedXP, setAnimatedXP] = useState(0);
  const [showStats, setShowStats] = useState(false);
  const { playLevelUp } = useGameSounds();
  
  const accuracy = Math.round((correctAnswers / totalQuestions) * 100);
  const isPerfect = accuracy === 100;
  
  // Messages personnalisés selon la performance - now with translations
  const getMessage = () => {
    if (isPerfect) return { 
      title: (t?.lesson?.perfect || "Parfait !") + " 🌟", 
      subtitle: t?.lesson?.perfectSub || "Tu as tout réussi sans erreur !" 
    };
    if (accuracy >= 90) return { 
      title: (t?.lesson?.excellent || "Excellent !") + " 🎉", 
      subtitle: t?.lesson?.excellentSub || "Tu maîtrises vraiment ce sujet !" 
    };
    if (accuracy >= 70) return { 
      title: (t?.lesson?.goodJob || "Bien joué !") + " 👏", 
      subtitle: t?.lesson?.goodJobSub || "Continue comme ça !" 
    };
    if (accuracy >= 50) return { 
      title: (t?.lesson?.notBad || "Pas mal !") + " 💪", 
      subtitle: t?.lesson?.notBadSub || "Tu progresses, continue !" 
    };
    return { 
      title: (t?.lesson?.keepGoing || "Continue !") + " 📚", 
      subtitle: t?.lesson?.keepGoingSub || "La pratique fait le maître !" 
    };
  };
  
  const message = getMessage();
  
  useEffect(() => {
    // Jouer le son de victoire
    playLevelUp();
    
    // Afficher les confettis
    setShowConfetti(true);
    
    // Animer le XP
    const xpInterval = setInterval(() => {
      setAnimatedXP(prev => {
        if (prev >= xpEarned) {
          clearInterval(xpInterval);
          return xpEarned;
        }
        return prev + Math.ceil(xpEarned / 20);
      });
    }, 50);
    
    // Afficher les stats avec délai
    setTimeout(() => setShowStats(true), 500);
    
    return () => clearInterval(xpInterval);
  }, [xpEarned, playLevelUp]);
  
  return (
    <div className="fixed inset-0 bg-gradient-to-b from-emerald-500 to-emerald-600 flex items-center justify-center z-40">
      <Confetti active={showConfetti} duration={3000} />
      
      <div className="text-center text-white max-w-md mx-auto px-6">
        {/* Hibou mascotte */}
        <div className="mb-4">
          <svg 
            viewBox="0 0 200 170" 
            className="w-28 h-24 mx-auto drop-shadow-lg"
          >
            {/* Corps du hibou */}
            <ellipse cx="100" cy="100" rx="45" ry="50" fill="#8B6914"/>
            
            {/* Ventre */}
            <ellipse cx="100" cy="108" rx="32" ry="36" fill="#F5DEB3"/>
            <ellipse cx="100" cy="112" rx="25" ry="28" fill="#FAEBD7"/>
            
            {/* Tête */}
            <ellipse cx="100" cy="55" rx="40" ry="35" fill="#8B6914"/>
            
            {/* Oreilles */}
            <path d="M 62 35 Q 65 18 78 32" fill="#6B4F12"/>
            <path d="M 138 35 Q 135 18 122 32" fill="#6B4F12"/>
            
            {/* Face claire */}
            <ellipse cx="100" cy="57" rx="32" ry="27" fill="#FAEBD7"/>
            
            {/* Yeux */}
            <ellipse cx="82" cy="54" rx="12" ry="13" fill="white"/>
            <ellipse cx="118" cy="54" rx="12" ry="13" fill="white"/>
            <ellipse cx="84" cy="56" rx="7" ry="8" fill="#2C1810"/>
            <ellipse cx="116" cy="56" rx="7" ry="8" fill="#2C1810"/>
            <circle cx="81" cy="52" r="3" fill="white"/>
            <circle cx="113" cy="52" r="3" fill="white"/>
            
            {/* Joues roses */}
            <ellipse cx="62" cy="62" rx="6" ry="4" fill="#FFB6C1" opacity="0.6"/>
            <ellipse cx="138" cy="62" rx="6" ry="4" fill="#FFB6C1" opacity="0.6"/>
            
            {/* Bec */}
            <path d="M 100 66 L 94 76 L 100 73 L 106 76 Z" fill="#FF8C00"/>
            
            {/* Grand sourire */}
            <path d="M 90 80 Q 100 90 110 80" stroke="#5D4E37" strokeWidth="2" fill="none" strokeLinecap="round"/>
            
            {/* Ailes */}
            <ellipse cx="55" cy="95" rx="10" ry="25" fill="#6B4F12"/>
            <ellipse cx="145" cy="95" rx="10" ry="25" fill="#6B4F12"/>
            
            {/* Pattes */}
            <ellipse cx="85" cy="152" rx="10" ry="6" fill="#FF8C00"/>
            <ellipse cx="115" cy="152" rx="10" ry="6" fill="#FF8C00"/>
          </svg>
          
          {/* Étoiles décoratives */}
          <div className="flex justify-center gap-2 -mt-1">
            <span className="text-lg">✨</span>
            <span className="text-xl">⭐</span>
            <span className="text-lg">✨</span>
          </div>
        </div>
        
        {/* Message de félicitation */}
        <h1 className="text-4xl font-bold mb-2">
          {message.title}
        </h1>
        <p className="text-xl opacity-90 mb-8">
          {message.subtitle}
        </p>
        
        {/* Titre de la leçon */}
        <p className="text-sm opacity-75 mb-6 px-4">
          {lessonTitle}
        </p>
        
        {/* Statistiques */}
        <div className={`grid grid-cols-3 gap-4 mb-6 transition-all duration-500 ${showStats ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {/* XP Gagnés */}
          <div className="bg-white/20 backdrop-blur rounded-2xl p-4">
            <Zap className="w-8 h-8 mx-auto mb-2 text-yellow-300" fill="currentColor" />
            <div className="text-3xl font-bold">{animatedXP}</div>
            <div className="text-xs opacity-80">{t?.lesson?.xpGained || "XP gagnés"}</div>
          </div>
          
          {/* Précision */}
          <div className="bg-white/20 backdrop-blur rounded-2xl p-4">
            <Target className="w-8 h-8 mx-auto mb-2 text-green-300" />
            <div className="text-3xl font-bold">{accuracy}%</div>
            <div className="text-xs opacity-80">{t?.lesson?.accuracy || "Précision"}</div>
          </div>
          
          {/* Score */}
          <div className="bg-white/20 backdrop-blur rounded-2xl p-4">
            <Star className="w-8 h-8 mx-auto mb-2 text-orange-300" fill="currentColor" />
            <div className="text-3xl font-bold">{correctAnswers}/{totalQuestions}</div>
            <div className="text-xs opacity-80">{t?.lesson?.correct || "Correct"}</div>
          </div>
        </div>
        
        {/* Contenu éducatif "Pour aller plus loin" */}
        {lessonContent && (
          <div className={`bg-white/10 backdrop-blur rounded-2xl p-5 mb-6 text-left max-h-64 overflow-y-auto transition-all duration-500 ${showStats ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-5 h-5 text-white" />
              <h3 className="font-bold text-lg text-white">{t?.lesson?.learnMore || "Pour aller plus loin"}</h3>
            </div>
            <div className="text-white/90 text-sm leading-relaxed whitespace-pre-line">
              {lessonContent}
            </div>
          </div>
        )}
        
        {/* Boutons */}
        <div className="space-y-3">
          {hasNextLesson ? (
            <Button 
              onClick={onNextLesson}
              className="w-full bg-white text-emerald-600 hover:bg-gray-100 font-bold py-4 text-lg rounded-xl shadow-lg transform transition hover:scale-105"
            >
              <span>{t?.lesson?.nextLesson || "Leçon suivante"}</span>
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          ) : null}
          
          <Button 
            onClick={onBackToDashboard}
            variant="ghost"
            className="w-full text-white/70 hover:text-white hover:bg-white/10 py-3 rounded-xl"
          >
            <Home className="w-5 h-5 mr-2" />
            <span>{t?.lesson?.backToDashboard || "Retour au tableau de bord"}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LessonComplete;
